/**
 * ActivationNudge — a thin, contextual banner driven by the activation spine.
 *
 * Shows the single most useful next step for where the account is in its
 * lifecycle, anchored on the real dollars Waypoint has surfaced. Deliberately
 * narrow so it doesn't nag: nothing for pre_onboard (the OnboardingWizard owns
 * that) and nothing for paying customers (only trials get the upgrade nudge).
 * Dismissible per-state — re-appears when the account advances to a new stage.
 */
import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivation, currentState, dollarsSurfaced } from '@/lib/hooks/useActivation';
import { useSubscription } from '@/lib/hooks/use-me';
import { useBasePath } from '@/lib/config/nav-helpers';
import { emit } from '@/lib/analytics';

function money(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

interface Nudge {
  text: string;
  ctaLabel: string;
  to: string; // path to navigate to on CTA
  urgent?: boolean;
}

export function ActivationNudge() {
  const activation = useActivation();
  const sub = useSubscription();
  const basePath = useBasePath();
  const navigate = useNavigate();
  const [, force] = useReducer((x) => x + 1, 0);

  const state = activation.activation_state; // high-water mark
  const live = currentState(activation); // where it stands right now
  const dollars = dollarsSurfaced(activation);
  const trialing = sub.plan === 'trial' || sub.status === 'trialing';
  // Keyed on both: a broken connection must re-raise the banner for an account
  // that already dismissed the nudge for its high-water stage.
  const key = `wp:nudge:${state}:${live}`;
  const dismissed = typeof window !== 'undefined' && window.localStorage.getItem(key) === '1';

  const nudge = buildNudge({ state, live, dollars, trialing, basePath });

  // Fire the "viewed" event once per shown state.
  useEffect(() => {
    if (nudge && !dismissed) {
      emit('activation_nudge_viewed', { activation_state: state, dollars_surfaced: dollars });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, !!nudge, dismissed]);

  if (!nudge || dismissed) return null;

  function onCta() {
    emit('activation_nudge_click', { activation_state: state, dollars_surfaced: dollars, to: nudge!.to });
    navigate(nudge!.to);
  }
  function dismiss() {
    window.localStorage.setItem(key, '1');
    emit('activation_nudge_dismissed', { activation_state: state });
    force();
  }

  return (
    <div
      className={`flex items-center justify-between gap-3 border-b px-4 py-2 ${
        nudge.urgent ? 'border-warn/20 bg-warn-soft/40' : 'border-primary/10 bg-primary-soft/40'
      }`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span className={`relative h-1.5 w-1.5 shrink-0 rounded-full ${nudge.urgent ? 'bg-warn' : 'bg-primary-bright'}`} />
        <span className="truncate text-xs text-ink md:text-sm">{nudge.text}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          onClick={onCta}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            nudge.urgent ? 'bg-warn text-on-fill hover:brightness-110' : 'bg-primary text-white hover:brightness-110'
          }`}
        >
          {nudge.ctaLabel}
        </button>
        <button
          onClick={dismiss}
          className="flex h-6 w-6 items-center justify-center rounded-full text-ink-dim transition-colors hover:bg-surface-2 hover:text-ink-muted"
          aria-label="Dismiss"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function buildNudge(input: {
  state: string;
  live: string;
  dollars: number;
  trialing: boolean;
  basePath: string;
}): Nudge | null {
  const { state, live, dollars, trialing, basePath } = input;
  // Nothing is reaching us any more, but this account HAS connected before:
  // the stage copy below would talk about value from data that stopped
  // arriving. Before 2026-10-21 activation_state itself fell to pre_onboard
  // here and the nudge showed nothing at all — and the OnboardingWizard
  // doesn't step in either, because its gate counts any row with
  // status='active', which includes a mirrored calendar row whose parent
  // Google grant is dead.
  if (live === 'pre_onboard' && state !== 'pre_onboard') {
    return {
      text: 'Waypoint has stopped receiving data — a connection needs reauthorising.',
      ctaLabel: 'Reconnect',
      to: `${basePath}/settings?tab=integrations`,
      urgent: true,
    };
  }
  switch (state) {
    case 'pre_onboard':
      // The OnboardingWizard owns first-tool setup — don't double-nudge.
      return null;
    case 'tool_connected':
      return {
        text: "Your tools are connected — open today's briefing to see what Waypoint found.",
        ctaLabel: 'View briefing',
        to: basePath,
      };
    case 'first_value':
    case 'activated':
    case 'power_user': {
      if (!trialing) return null; // never nag paying customers
      if (dollars > 0) {
        return {
          text: `Waypoint has surfaced ${money(dollars)} of risk & trapped cash — keep every model running.`,
          ctaLabel: 'See plans',
          to: `${basePath}/upgrade`,
          urgent: state === 'power_user',
        };
      }
      return {
        text: "You're getting real value from Waypoint — lock in your plan before your trial ends.",
        ctaLabel: 'See plans',
        to: `${basePath}/upgrade`,
      };
    }
    default:
      return null;
  }
}
